import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Eye, ChevronDown, ChevronUp } from "lucide-react";
import PersonalDetailsForm, {
  EMPTY_PERSONAL_DETAILS,
  validatePersonalDetails,
} from "@/components/PersonalDetailsForm";

const ROLE_LABELS = {
  worker: "Worker (compliance owner)",
  team_member: "Team member (reviewer)",
  manager: "Manager (full access)",
};

const DEFAULT_WORKER_ROLE_OPTIONS = [
  { key: "healthcare_assistant", label: "Healthcare Assistant" },
  { key: "senior_care_assistant", label: "Senior Care Assistant" },
  { key: "registered_nurse", label: "Registered Nurse" },
  { key: "chef", label: "Chef" },
  { key: "kitchen_assistant", label: "Kitchen Assistant" },
];

export default function InviteDialog({ open, onOpenChange, onCreated, defaultRole, availableRoles }) {
  const roles = availableRoles || ["worker", "team_member", "manager"];
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState(defaultRole || roles[0] || "worker");
  const [workerRole, setWorkerRole] = useState("healthcare_assistant");
  const [locationId, setLocationId] = useState("");
  const [locations, setLocations] = useState([]);
  const [mode, setMode] = useState("email");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [workerRoleOptions, setWorkerRoleOptions] = useState(DEFAULT_WORKER_ROLE_OPTIONS);
  const [personalDetails, setPersonalDetails] = useState({ ...EMPTY_PERSONAL_DETAILS });
  const [pdExpanded, setPdExpanded] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/config/roles").then(({ data }) => {
      if (data?.worker_roles) {
        setWorkerRoleOptions(
          data.worker_roles.map((r) => ({ key: r.key || r.value || r, label: r.label || r.name || r }))
        );
      }
    }).catch(() => {});
    api.get("/locations").then(({ data }) => setLocations(data || [])).catch(() => setLocations([]));
  }, [open]);

  useEffect(() => {
    if (defaultRole && open) setRole(defaultRole);
  }, [defaultRole, open]);

  const reset = () => {
    setName("");
    setEmail("");
    setRole(defaultRole || roles[0] || "worker");
    setWorkerRole("healthcare_assistant");
    setLocationId("");
    setMode("email");
    setPassword("");
    setBusy(false);
    setPersonalDetails({ ...EMPTY_PERSONAL_DETAILS });
    setPdExpanded(false);
  };

  // Mirror the top-of-form name/email into the personal-details block so
  // the manager only types them once.
  useEffect(() => {
    setPersonalDetails((prev) => ({
      ...prev,
      full_legal_name: prev.full_legal_name || name,
      email: email || prev.email,
    }));
  }, [name, email]);

  const submit = async () => {
    if (!name || !email) {
      toast.error("Please fill in name and email");
      return;
    }
    if (role === "worker" && !workerRole) {
      toast.error("Choose a worker role");
      return;
    }
    if (mode === "manual" && password.length < 6) {
      toast.error("Starter password must be at least 6 characters");
      return;
    }
    // Personal details are required when creating a NEW candidate (worker).
    if (role === "worker") {
      const pdErrors = validatePersonalDetails(personalDetails, { emailEditable: false });
      if (Object.keys(pdErrors).length > 0) {
        setPdExpanded(true);
        toast.error("Please complete personal details", {
          description: "Every field is required to create a candidate profile.",
        });
        return;
      }
    }
    setBusy(true);
    try {
      const body = { name, email, role, send_invite: mode === "email" };
      if (role === "worker") body.worker_role = workerRole;
      if (locationId) body.location_id = locationId;
      if (mode === "manual") body.password = password;
      if (role === "worker") {
        body.personal_details = {
          full_legal_name: personalDetails.full_legal_name.trim(),
          mobile: personalDetails.mobile.trim(),
          home_address: personalDetails.home_address.trim(),
          date_of_birth: personalDetails.date_of_birth,
          nationality: personalDetails.nationality.trim(),
        };
      }
      const { data } = await api.post("/team/invite", body);
      const roleLabel = role === "worker" ? "Worker" : role === "team_member" ? "Team member" : "Manager";
      if (mode === "email") {
        toast.success(`${roleLabel} invited`, { description: `${email} will get an email to activate their account.` });
      } else {
        toast.success(`${roleLabel} added`, {
          description: data?.invite_status === "pending"
            ? `${email} will get an email to set their password.`
            : `${email} can sign in now with the password you set.`,
        });
      }
      reset();
      onOpenChange(false);
      onCreated && onCreated();
    } catch (e) {
      toast.error("Could not invite", { description: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setBusy(false);
    }
  };

  const openPreview = async () => {
    if (!name || !email) {
      toast.error("Add a name and email first");
      return;
    }
    setPreviewLoading(true);
    try {
      const body = { name, email, role, send_invite: true };
      if (role === "worker") body.worker_role = workerRole;
      const { data } = await api.post("/team/invite/preview", body);
      setPreview(data);
    } catch (e) {
      toast.error("Could not load preview", { description: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="bg-white sm:max-w-lg max-h-[92vh] overflow-y-auto" data-testid="invite-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Invite someone to the workspace</DialogTitle>
          <DialogDescription>
            Send an email invite (they set their own password) or add someone manually with a starter password.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {roles.length > 1 && (
            <div className="space-y-1.5">
              <Label className="label-caps">Role</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {roles.map((r) => (
                    <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="label-caps">Full name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="invite-name" placeholder="Sarah Patel" />
            </div>
            <div className="space-y-1.5">
              <Label className="label-caps">Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="invite-email" placeholder="sarah@example.com" />
            </div>
          </div>

          {role === "worker" && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="label-caps">Job role</Label>
                <Select value={workerRole} onValueChange={setWorkerRole}>
                  <SelectTrigger data-testid="invite-worker-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {workerRoleOptions.map((r) => (
                      <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {locations.length > 0 && (
                <div className="space-y-1.5">
                  <Label className="label-caps">Location (optional)</Label>
                  <Select value={locationId || "none"} onValueChange={(v) => setLocationId(v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="invite-location"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      {locations.map((l) => (
                        <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}

          <div className="pt-2">
            <div className="label-caps mb-2">How should we onboard them?</div>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setMode("email")}
                data-testid="invite-mode-email"
                className={`text-left rounded-lg border p-3 transition ${
                  mode === "email"
                    ? "border-teal-600 bg-teal-50 ring-1 ring-teal-600"
                    : "border-slate-200 hover:border-slate-300 bg-white"
                }`}
              >
                <div className="text-xs uppercase tracking-widest font-semibold text-teal-700">Recommended</div>
                <div className="font-semibold mt-1 text-slate-900">Send email invite</div>
                <div className="text-xs text-slate-600 mt-1">They click a link and choose their own password.</div>
              </button>
              <button
                type="button"
                onClick={() => setMode("manual")}
                data-testid="invite-mode-manual"
                className={`text-left rounded-lg border p-3 transition ${
                  mode === "manual"
                    ? "border-teal-600 bg-teal-50 ring-1 ring-teal-600"
                    : "border-slate-200 hover:border-slate-300 bg-white"
                }`}
              >
                <div className="text-xs uppercase tracking-widest font-semibold text-slate-500">Manual add</div>
                <div className="font-semibold mt-1 text-slate-900">Set a starter password</div>
                <div className="text-xs text-slate-600 mt-1">Useful when you&apos;re sitting with them at induction.</div>
              </button>
            </div>
          </div>

          {mode === "manual" && (
            <div className="space-y-1.5">
              <Label className="label-caps">Starter password</Label>
              <Input
                type="text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="invite-password"
                placeholder="At least 6 characters"
              />
              <p className="text-xs text-slate-500">Share this with them privately — they can change it after signing in. Audentra will also email them a link to reset it if they prefer.</p>
            </div>
          )}

          {role === "worker" && (
            <div className="rounded-lg border border-slate-200 bg-slate-50/50" data-testid="invite-personal-details">
              <button
                type="button"
                onClick={() => setPdExpanded((v) => !v)}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-slate-100 transition-colors rounded-lg"
                data-testid="invite-personal-details-toggle"
              >
                <div className="text-left">
                  <div className="font-semibold text-sm text-slate-900">Personal details</div>
                  <div className="text-xs text-slate-500 mt-0.5">Required to create a candidate profile.</div>
                </div>
                {pdExpanded ? (
                  <ChevronUp className="h-4 w-4 text-slate-500" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-slate-500" />
                )}
              </button>
              {pdExpanded && (
                <div className="px-4 pb-4 pt-1">
                  <PersonalDetailsForm
                    value={personalDetails}
                    onChange={setPersonalDetails}
                    accountEmail={email}
                    emailEditable={false}
                    embed
                  />
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          {mode === "email" && (
            <Button
              variant="outline"
              type="button"
              onClick={openPreview}
              disabled={previewLoading || !name || !email}
              data-testid="invite-preview"
              className="mr-auto sm:mr-0"
            >
              <Eye className="h-4 w-4 mr-1.5" /> {previewLoading ? "Loading…" : "Preview email"}
            </Button>
          )}
          <Button onClick={submit} disabled={busy} className="bg-teal-700 hover:bg-teal-800" data-testid="invite-submit">
            {busy ? "Sending…" : mode === "email" ? "Send invite" : `Add ${role === "worker" ? "worker" : role === "team_member" ? "team member" : "manager"}`}
          </Button>
        </DialogFooter>
      </DialogContent>

      <EmailPreviewDialog open={!!preview} onClose={() => setPreview(null)} preview={preview} />
    </Dialog>
  );
}


function EmailPreviewDialog({ open, onClose, preview }) {
  if (!preview) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-white sm:max-w-2xl" data-testid="invite-preview-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Preview invite email</DialogTitle>
          <DialogDescription>This is the exact email your invitee will receive.</DialogDescription>
        </DialogHeader>
        <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-700 space-y-1">
          <div><span className="font-semibold text-slate-500 uppercase tracking-widest text-[10px]">From</span>&nbsp; <span className="font-mono">{preview.sender}</span></div>
          <div><span className="font-semibold text-slate-500 uppercase tracking-widest text-[10px]">To</span>&nbsp; <span className="font-mono">{preview.recipient}</span></div>
          <div><span className="font-semibold text-slate-500 uppercase tracking-widest text-[10px]">Subject</span>&nbsp; <span className="font-semibold text-slate-900">{preview.subject}</span></div>
        </div>
        <div className="max-h-[420px] overflow-y-auto rounded-md border border-slate-200 bg-white">
          <iframe
            title="Email preview"
            data-testid="invite-preview-iframe"
            className="w-full h-[420px] border-0"
            srcDoc={preview.html}
            sandbox=""
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="invite-preview-close">Close preview</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
